import { create, get } from "./apiService";

export type BusinessPayLinkStatus =
  | "pending"
  | "processing"
  | "paid"
  | "confirmed"
  | "completed"
  | "expired"
  | "cancelled";

export type PayLinkPaymentMethod = "88pay" | "crypto";

export interface CreatePayLinkRequest {
  amount: number;
  asset: string;
  currency: string;
  reference: string;
  description: string;
  paymentMethod: PayLinkPaymentMethod;
  recipientName: string;
  recipientSurname: string;
  cryptoAssetId?: string;
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

export interface PayLinkRowResponse {
  _id?: string;
  payLinkId?: string;
  payLinkUrl?: string;
  qrCode?: string;
  recipientName?: string;
  recipientSurname?: string;
  description?: string;
  reference?: string;
  amount?: number | string | { $numberDecimal: string };
  currency?: string;
  status?: BusinessPayLinkStatus | string;
  paymentMethod?: PayLinkPaymentMethod | string;
  expiresAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PayLinkPaginationResponse {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface PayLinkListResponse {
  payLinks?: PayLinkRowResponse[];
  pagination?: PayLinkPaginationResponse;
}

export interface ListPayLinksParams {
  page?: number;
  limit?: number;
  status?: string;
}

export interface PublicPayLinkPageResponse {
  payLinkId: string;
  businessName?: string;
  businessLogo?: string;
  recipientName?: string;
  recipientSurname?: string;
  reference?: string;
  description?: string;
  amount: number | string | { $numberDecimal: string };
  currency?: string;
  status: BusinessPayLinkStatus | string;
  paymentMethod: PayLinkPaymentMethod;
  cryptoAsset?: {
    _id?: string;
    symbol?: string;
    name?: string;
    image?: string | null;
  } | null;
  walletAddress?: string;
  expiresAt?: string;
  acceptedCurrencies?: Array<{
    _id?: string;
    symbol?: string;
    name?: string;
    image?: string | null;
    type?: string;
  }>;
  deepLinks: {
    mobile: string;
    web: string;
  };
}

export interface ProcessPublicPayLinkRequest {
  paymentMethod: PayLinkPaymentMethod;
  email?: string;
  phone?: string;
}

export interface ProcessPublicPayLinkResponse {
  status: string;
  paidAt: string | null;
  paymentMethod: PayLinkPaymentMethod;
}

function businessWalletPaylinksPath(businessId: string, suffix = ""): string {
  const base = `/api/v2/business/${encodeURIComponent(
    businessId
  )}/wallet/paylinks`;
  return suffix ? `${base}/${encodeURIComponent(suffix)}` : base;
}

export const createPayLink = async (
  businessId: string,
  data: CreatePayLinkRequest
): Promise<PayLinkRowResponse> => {
  return create<PayLinkRowResponse>(
    businessWalletPaylinksPath(businessId),
    data
  );
};

export const listPayLinks = async (
  businessId: string,
  params: ListPayLinksParams = {}
): Promise<PayLinkListResponse> => {
  const query: Record<string, string> = {};
  if (params.page != null) {
    query.page = String(Math.max(1, Number(params.page) || 1));
  }
  if (params.limit != null) {
    query.limit = String(Math.max(1, Math.floor(Number(params.limit)) || 1));
  }
  if (params.status != null && String(params.status).trim() !== "") {
    query.status = String(params.status).trim();
  }

  return get<PayLinkListResponse>(
    businessWalletPaylinksPath(businessId),
    query as Record<string, unknown>
  );
};

export const getPayLinkById = async (
  businessId: string,
  payLinkId: string
): Promise<PayLinkRowResponse> => {
  return get<PayLinkRowResponse>(
    businessWalletPaylinksPath(businessId, payLinkId)
  );
};

export const cancelPayLink = async (
  businessId: string,
  payLinkId: string
): Promise<PayLinkRowResponse> => {
  return create<PayLinkRowResponse>(
    `${businessWalletPaylinksPath(businessId, payLinkId)}/cancel`,
    {}
  );
};

export const getPublicPayLinkPage = async (
  payLinkId: string
): Promise<PublicPayLinkPageResponse> => {
  return get<PublicPayLinkPageResponse>(
    `/api/v2/pay/${encodeURIComponent(payLinkId)}`
  );
};

export const processPublicPayLink = async (
  payLinkId: string,
  data: ProcessPublicPayLinkRequest
): Promise<ProcessPublicPayLinkResponse> => {
  return create<ProcessPublicPayLinkResponse>(
    `/api/v2/pay/${encodeURIComponent(payLinkId)}/process`,
    data
  );
};
